import { PublishStatus } from '@src/types.ts/publishRecord';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import InfoIcon from '../../svgs/InfoIconAlt';

import type { VideoAction, VideoVersion } from '../../types.ts/story';
import type { PublishRecord } from '@src/types.ts/publishRecord';

export const hasPublishAsLastAction = (lastAction?: VideoAction): boolean => {
  return (
    !!lastAction?.type &&
    [
      'publish',
      'system_render_start',
      'system_render_update',
      'system_render_error',
      'system_render_confirm',
    ].includes(lastAction.type)
  );
};

const isTranslatedPublish = (lastAction?: VideoAction): boolean =>
  lastAction?.type === 'publish' && !!lastAction.groupId;

/** A translated publish's details come from its records, so it stands alone. */
const isAggregatable = (lastAction?: VideoAction): boolean =>
  hasPublishAsLastAction(lastAction) && !isTranslatedPublish(lastAction);

export type AggregateVideoVersion = VideoVersion & {
  pastVersions?: VideoVersion[];
};

export const aggregateVersionsOfTheSameLastAction = (
  videoVersions?: VideoVersion[],
): AggregateVideoVersion[] => {
  let result: AggregateVideoVersion[] = [];
  if (!videoVersions?.length) {
    return result;
  }
  for (let i = 0; i < videoVersions.length; i++) {
    const aggregate: AggregateVideoVersion = { ...videoVersions[i] };
    if (isAggregatable(videoVersions[i].lastActionJson)) {
      aggregate.pastVersions = [];
      for (let j = i + 1; j < videoVersions.length; j++) {
        if (isAggregatable(videoVersions[j].lastActionJson)) {
          aggregate.pastVersions.push(videoVersions[j]);
          i++;
        } else {
          break;
        }
      }
    }
    result.push(aggregate);
  }
  return result;
};

type DetailRow = { key: string; label: string; time: string };

export const SameLastActionAggregation = ({
  allVersions,
  formattedAction,
  editor,
  sidebarRef,
}: {
  allVersions: VideoVersion[];
  formattedAction: string;
  editor: string;
  sidebarRef: React.RefObject<HTMLDivElement>;
}) => (
  <HistoryDetails
    title={`${formattedAction} by ${editor}`}
    rows={[...allVersions].reverse().map(v => ({
      key: v.versionId,
      label: toDetailedFormattedAction(v.lastActionJson),
      time: toDetailedTime(v.lastActionJson?.date),
    }))}
    sidebarRef={sidebarRef}
  />
);

/**
 * A translated publish's details. Its render never writes the Video, so its
 * stages are not versions to aggregate — they are read from its PublishRecords.
 */
export const PublishRecordAggregation = ({
  records,
  formattedAction,
  editor,
  sidebarRef,
}: {
  /** The group's records, live. Empty until fetched or created. */
  records: PublishRecord[];
  formattedAction: string;
  editor: string;
  sidebarRef: React.RefObject<HTMLDivElement>;
}) => (
  <HistoryDetails
    title={`${formattedAction} by ${editor}`}
    rows={
      records.length
        ? records.flatMap(toPublishRecordRows)
        : [{ key: 'loading', label: 'Loading...', time: '' }]
    }
    sidebarRef={sidebarRef}
  />
);

const toPublishRecordRows = (record: PublishRecord): DetailRow[] => {
  const rows: DetailRow[] = [
    {
      key: `${record.id}-requested`,
      label: 'Publish Requested',
      time: toDetailedTime(record.publishedAt),
    },
  ];
  if (record.renderStartedAt) {
    rows.push({
      key: `${record.id}-started`,
      label: 'Published Start',
      time: toDetailedTime(record.renderStartedAt),
    });
  }
  if (record.publishStatus === PublishStatus.Succeeded) {
    rows.push({
      key: `${record.id}-done`,
      label: 'Published Complete',
      time: toDetailedTime(record.renderCompletedAt),
    });
  } else if (record.publishStatus === PublishStatus.Failed) {
    rows.push({
      key: `${record.id}-done`,
      label: 'Published Error',
      time: toDetailedTime(record.renderCompletedAt),
    });
  }
  return rows;
};

const HistoryDetails = ({
  title,
  rows,
  sidebarRef,
}: {
  title: string;
  rows: DetailRow[];
  sidebarRef: React.RefObject<HTMLDivElement>;
}) => {
  const [tooltip, setTooltip] = useState<DOMRect | null>(null);
  const [topForUpwardPos, setTopForUpwardPos] = useState<number | null>(null);
  const [isTooltipVisible, setIsTooltipVisible] = useState<boolean>(false);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsTooltipVisible(false);

    if (tooltip && tooltipRef.current) {
      if (!sidebarRef?.current) {
        setTopForUpwardPos(null);
        setIsTooltipVisible(true);
        return;
      }

      const sidebarRect = sidebarRef.current.getBoundingClientRect();
      const top = tooltip.top + 15;
      const height = tooltipRef.current.getBoundingClientRect().height || 186;
      const hasEnoughSpaceBot = sidebarRect.bottom - top >= height;

      setTopForUpwardPos(hasEnoughSpaceBot ? null : tooltip.top - height);
      setIsTooltipVisible(true);
    }
  }, [tooltip]);

  return (
    <Container
      onMouseEnter={e => {
        setTooltip(e.currentTarget.getBoundingClientRect());
      }}
      onMouseMove={e => {
        setTooltip(e.currentTarget.getBoundingClientRect());
      }}
      onMouseLeave={() => {
        setTooltip(null);
      }}
    >
      <InfoIcon width="16" height="16" />
      {tooltip && (
        <Tooltip
          top={topForUpwardPos == null ? tooltip.top + 15 : topForUpwardPos}
          left={tooltip.left - 250 - 25}
          onClick={event => event.stopPropagation()}
          ref={tooltipRef}
          isVisible={isTooltipVisible}
        >
          <TooltipHeader>
            <TooltipHeaderText>History Details</TooltipHeaderText>
          </TooltipHeader>
          <TooltipBody>
            <LastAction>{title}</LastAction>
            {rows.map(row => (
              <VersionAction key={row.key}>
                <span>{row.label}</span>
                <VersionTime>{row.time}</VersionTime>
              </VersionAction>
            ))}
          </TooltipBody>
        </Tooltip>
      )}
    </Container>
  );
};

const Container = styled.div`
  position: absolute;
  width: 16px;
  height: 16px;
  right: 32px;
  top: 0;
  bottom: 0;
  margin: auto;
  color: var(--text-muted);

  &:hover {
    color: var(--foreground);
  }
`;

const Tooltip = styled.div<
  Record<'top' | 'left', number> & {
    isVisible?: boolean;
  }
>`
  padding: var(--spacing-md);
  border-radius: var(--radius);
  z-index: 100005;
  position: fixed;
  background: var(--background);
  border: 1px solid var(--border);
  box-shadow: var(--elevation-toast);
  width: 250px;
  font-family: var(--font-primary);
  color: var(--foreground);
  opacity: ${props => (props.isVisible ? '1' : '0')};
  top: ${props => props.top}px;
  left: ${props => props.left}px;
`;

const TooltipHeader = styled.div`
  display: flex;
  border: none;
  border-bottom: 1px solid var(--border);
  padding-bottom: var(--spacing-sm);
  align-items: center;
`;

const TooltipHeaderText = styled.div`
  font-weight: var(--font-weight-semi-bold);
  font-size: var(--text-sm);
  color: var(--foreground);
`;

const TooltipBody = styled.div`
  padding: var(--spacing-sm) 0 0 0;
  color: var(--text-secondary);
`;

const LastAction = styled.div`
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  color: var(--foreground);
`;

const VersionAction = styled.div`
  font-size: var(--text-xs);
  font-weight: var(--font-weight-normal);
  display: flex;
  justify-content: space-between;
  margin-top: var(--spacing-sm);
`;

const VersionTime = styled.span`
  color: var(--text-muted);
`;

const toDetailedFormattedAction = (lastAction?: VideoAction): string => {
  if (!lastAction?.type) {
    return 'Modified';
  }
  return {
    save: 'Saved',
    autosave: 'Autosaved',
    unsave: 'Unsaved',
    publish: 'Published Complete',
    system_render_start: 'Publish Requested',
    system_render_update: 'Published Start',
    system_render_error: 'Published Error',
    system_render_confirm: 'Published Reconfirmed',
  }[lastAction.type];
};

const toDetailedTime = (date?: number | string | null): string => {
  if (!date) {
    return '--:--';
  }
  return new Date(date).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
};
